import * as React from 'react';
import {View} from 'react-native';
import {get} from 'lodash';
import isEmpty from 'lodash/isEmpty';

import FormattedNumber from '/component/base/FormattedNumber/FormattedNumber';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {UserProfileModal} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {
  generateCallback,
  generateComponentCallback,
} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ClientSearchContent.style';
interface Order {
  id: number;
  orderNumber: number;
  orderedOn: string;
  price: {
    total?: number | string;
    currencyCode?: string;
  };
}
interface ClientSearchContentProps {
  recordData: {
    recentOrders?: Order[];
  };
  onOrderPress: (data: {orderId: number | undefined}) => void;
}

export const ClientSearchContent: React.FC<ClientSearchContentProps> = ({
  recordData = {},
  onOrderPress = () => {},
}) => {
  const {tt} = useTranslation('reportCardContent.clientSearch');
  const recentOrders = get(recordData, 'recentOrders', []);

  const renderEmptyOrderRow = generateComponentCallback({
    component: (
      <View
        {...testProperties(UserProfileModal.orderHistoryNoItem)}
        style={styles.order}>
        <View style={styles.orderLine1}>
          <View style={styles.orderDate}>
            <Text>{tt('noOrderText', () => 'No Orders Yet')}</Text>
          </View>
          <View style={styles.orderAmount}>
            <Text style={styles.orderAmountEmptyText}>--</Text>
          </View>
        </View>
      </View>
    ),
  });

  const renderOrderRow = (order: Order, index: number) => {
    const id = get(order, 'id', 0);
    const orderNumber = get(order, 'orderNumber', 0);
    const orderedOn = get(order, 'orderedOn', '');
    const price = get(order, 'price', {});
    const total = get(price, 'total', '');
    const currencyCode = get(price, 'currencyCode', '');

    return (
      // @ts-expect-error TS(2322): Type '{ children: Element[]; key: any; style: View... Remove this comment to see the full error message
      <TouchableOpacity
        {...testProperties(UserProfileModal.orderHistoryItem, true)}
        key={index}
        style={styles.order}
        onPress={generateCallback({func: onOrderPress, params: {orderId: id}})}>
        <View style={styles.orderLine1}>
          <View style={styles.orderDate}>
            <Text testID={UserProfileModal.orderHistoryDate}>
              {getDateUtilDefaultInstance().formatDate(
                orderedOn,
                'MMMM d, yyyy',
              )}
            </Text>
          </View>
          <View style={styles.orderAmount}>
            <FormattedNumber
              testID={UserProfileModal.orderHistoryAmount}
              value={total}
              currencyCode={currencyCode}
              decimalPlaces={2}
              boldText={true}
            />
          </View>
        </View>
        <View style={styles.orderLine2}>
          {tt(
            'orderNumber',
            generateComponentCallback({
              component: (
                <Text
                  variant="caption"
                  emphasis="disabled"
                  testID={UserProfileModal.orderHistoryNumber}>
                  Order #:{orderNumber}
                </Text>
              ),
            }),
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <Gutter>
          <Text variant="heading" style={styles.headerText}>
            {tt('heading', 'Order History')}
          </Text>
        </Gutter>
      </View>
      <Gutter>
        {isEmpty(recentOrders)
          ? renderEmptyOrderRow()
          : recentOrders.map(renderOrderRow)}
      </Gutter>
    </View>
  );
};
